import type { GeometryKey } from './keys';

export const en: Record<GeometryKey, string> = {
  // 9 Experiments
  'experiment.angle-chase': 'Angle chase',
  'experiment.triangle-lab': 'Triangle lab',
  'experiment.pythagoras': 'Pythagoras',
  'experiment.similarity': 'Similarity',
  'experiment.circle-theorems': 'Circle theorems',
  'experiment.area-lab': 'Area lab',
  'experiment.transformations': 'Transformations',
  'experiment.coordinate-geometry': 'Coordinate geometry',
  'experiment.constructions': 'Constructions',
  // Common controls
  'action.reset': 'Reset',
  'action.randomise': 'Randomise',
  'action.show-me': 'Show me',
  'action.undo': 'Undo',
  'action.eraser': 'Eraser',
  'panel.advanced': 'Advanced',
  'panel.mode': 'Mode',
  'panel.tool': 'Tool',
  // Shared chrome — eyebrow + interaction hint
  'app.eyebrow': 'Math · 2D Geometry',
  'hint.line1': 'Drag the accent-coloured points to explore.',
  'hint.line2': 'The maths lives in the figure, not in text.',
  // Coordinate-geometry additions
  'coord.panel.ratio-m': 'Ratio m',
  'coord.panel.ratio-n': 'Ratio n',
  'coord.panel.show-angles': 'Show angles',
  // Transformations — symmetry redesign
  'transform.symmetry.drag-hint': 'Drag the mirror line',
  'transform.symmetry.axis-found': 'Symmetry axis ✓',
  'transform.symmetry.axes': 'Mirror lines',
  'transform.symmetry.rotation-order': 'Rotation order',
  'transform.symmetry.spin': 'Spin',
  // Constructions — tool options
  'construct.tool.compass': 'Compass',
  'construct.tool.straightedge': 'Straightedge',
  'construct.tool.eraser': 'Eraser',
  // Shared badges
  'badge.parallel': '∥',
  'badge.perpendicular': '⊥',
  'badge.similar': '~',
  'badge.invalid': '✗',
  // Angle-chase specific
  'angle.mode.around-a-point': 'Around a point',
  'angle.mode.parallel-lines': 'Parallel lines',
  'angle.mode.polygon-angles': 'Polygon angles',
  'angle.mode.double-transversal': 'Double transversal',
  'angle.panel.angles-on-line': 'Angles on a line',
  'angle.panel.show-angle-values': 'Show angle values',
  'angle.panel.snap-to-5': 'Snap to 5°',
  'angle.panel.n-gon-sides': 'Sides (n)',
  'angle.preset.straight-line': 'Straight line',
  'angle.preset.full-turn': 'Full turn',
  'angle.preset.z-angles': 'Z-angles',
  'angle.preset.c-angles': 'C-angles',
  'angle.preset.f-angles': 'F-angles',
  'angle.preset.pentagon-fan': 'Pentagon fan',
  'angle.preset.hexagon-turtle': 'Hexagon turtle walk',
  'angle.preset.double-transversal': 'Double transversal',
  // Triangle-lab specific
  'triangle.mode.free-drag': 'Free drag',
  'triangle.mode.inequality': 'Inequality',
  'triangle.mode.congruence': 'Congruence',
  'triangle.criterion.SSS': 'SSS',
  'triangle.criterion.SAS': 'SAS',
  'triangle.criterion.ASA': 'ASA',
  'triangle.criterion.AAS': 'AAS',
  'triangle.criterion.SSA': 'SSA',
  'triangle.panel.show-measurements': 'Show measurements',
  'triangle.panel.center-centroid': 'Centroid',
  'triangle.panel.center-circumcenter': 'Circumcenter',
  'triangle.panel.center-incenter': 'Incenter',
  'triangle.panel.center-orthocenter': 'Orthocenter',
  'triangle.preset.equilateral': 'Equilateral',
  'triangle.preset.isosceles-right': 'Isosceles right',
  'triangle.preset.scalene-obtuse': 'Scalene obtuse',
  'triangle.preset.euler-tour': 'Euler line tour',
  'triangle.preset.ssa-two': 'SSA: two solutions',
  'triangle.criterion': 'Criterion',
  // Pythagoras specific
  'pythagoras.mode.squares': 'Squares',
  'pythagoras.mode.converse': 'Converse',
  'pythagoras.mode.triples': 'Triples',
  'pythagoras.mode.shapes': 'Shapes',
  'pythagoras.panel.show-areas': 'Show areas',
  'pythagoras.panel.shape': 'Shape',
  'pythagoras.panel.scrubber': 'Proof scrubber',
  'pythagoras.shape.square': 'Square',
  'pythagoras.shape.semicircle': 'Semicircle',
  'pythagoras.shape.equilateral': 'Equilateral triangle',
  'pythagoras.preset.3-4-5': '3–4–5',
  'pythagoras.preset.5-12-13': '5–12–13',
  'pythagoras.preset.1-1-sqrt2': '1–1–√2',
  'pythagoras.preset.8-15-17': '8–15–17',
  'pythagoras.preset.almost-right-4-5-6': 'Almost right (4–5–6)',
  // Similarity specific
  'similarity.mode.dilation': 'Dilation',
  'similarity.mode.intercept': 'Intercept theorem',
  'similarity.mode.similar-triangles': 'Similar triangles',
  'similarity.panel.k': 'Scale factor k',
  'similarity.panel.show-rays': 'Show rays',
  'similarity.panel.show-area-grid': 'Show area grid',
  'similarity.preset.midsegment-half': 'Midsegment (k=½)',
  'similarity.preset.double-k-2': 'Double (k=2)',
  'similarity.preset.inversion-k-minus-1': 'Inversion (k=−1)',
  'similarity.preset.aa-detective': 'AA detective',
  // Circle-theorems specific
  'circle.mode.inscribed': 'Inscribed angle',
  'circle.mode.tangent': 'Tangent',
  'circle.mode.cyclic-quad': 'Cyclic quadrilateral',
  'circle.mode.alternate-segment': 'Alternate segment',
  'circle.mode.power-of-point': 'Power of a point',
  'circle.panel.show-central-angle': 'Show central angle',
  'circle.panel.snap-to-diameter': 'Snap to diameter',
  'circle.preset.thales': 'Thales (diameter)',
  'circle.preset.minor-arc': 'Minor arc',
  'circle.preset.crossed-chords': 'Crossed chords',
  'circle.preset.tangent-secant': 'Tangent–secant',
  // Area-lab specific
  'area.panel.shape': 'Shape',
  'area.panel.shear': 'Shear',
  'area.panel.sectors': 'Sectors (n)',
  'area.panel.polygon-n': 'Sides (n)',
  'area.panel.scrubber': 'Derive scrubber',
  'area.panel.show-grid': 'Show grid',
  'area.shape.parallelogram': 'Parallelogram',
  'area.shape.triangle': 'Triangle',
  'area.shape.trapezoid': 'Trapezoid',
  'area.shape.circle': 'Circle',
  'area.shape.regular-polygon': 'Regular polygon',
  'area.shape.composite': 'Composite (L-shape)',
  'area.shape.heron': 'Heron cross-check',
  'area.preset.parallelogram': 'Parallelogram (6×4)',
  'area.preset.triangle': 'Triangle (6×4)',
  'area.preset.trapezoid': 'Trapezoid (3–5, h=4)',
  'area.preset.circle': 'Circle (r=4)',
  'area.preset.hexagon-apothem': 'Hexagon apothem',
  'area.preset.l-shape': 'L-shape composite',
  'area.preset.heron-check': 'Heron check (5–6–7)',
  // Transformations specific
  'transform.mode.transform': 'Transform',
  'transform.mode.symmetry': 'Symmetry',
  'transform.mode.composition': 'Composition',
  'transform.mode.tessellation': 'Tessellation',
  'transform.picker.translate': 'Translate',
  'transform.picker.rotate': 'Rotate',
  'transform.picker.reflect': 'Reflect',
  'transform.picker.dilate': 'Dilate',
  'transform.panel.transform': 'Transform',
  'transform.panel.figure': 'Figure',
  'transform.panel.tile': 'Tile',
  'transform.panel.dilate-k': 'Scale factor k',
  'transform.tile.square': 'Square',
  'transform.tile.hexagon': 'Hexagon',
  'transform.tile.triangle': 'Triangle',
  'transform.tile.escher-square': 'Escher square',
  'transform.preset.quarter-turn': 'Quarter turn',
  'transform.preset.mirror-flip': 'Mirror flip',
  'transform.preset.parallel-mirrors': 'Parallel mirrors',
  'transform.preset.crossing-mirrors': 'Crossing mirrors',
  'transform.preset.hexagon-tiling': 'Hexagon tiling',
  // Coordinate-geometry specific
  'coord.mode.segment': 'Segment',
  'coord.mode.parallel-perpendicular': 'Parallel & perpendicular',
  'coord.mode.section': 'Section formula',
  'coord.mode.shoelace': 'Shoelace area',
  'coord.mode.locus': 'Locus (bisector)',
  'coord.panel.show-rise-run': 'Show rise/run triangle',
  'coord.panel.ratio-mn': 'Ratio m : n',
  'coord.preset.3-4-5-segment': '3–4–5 segment',
  'coord.preset.perpendicular-pair': 'Perpendicular pair',
  'coord.preset.section-2-1': 'Section 2 : 1',
  'coord.preset.lattice-pentagon': 'Lattice pentagon',
  // Constructions — target labels
  'construct.target.perpendicular-bisector': 'Perpendicular bisector',
  'construct.target.angle-bisector': 'Angle bisector',
  'construct.target.copy-segment': 'Copy segment',
  'construct.target.copy-angle': 'Copy angle',
  'construct.target.perpendicular-at-point': 'Perpendicular at point',
  'construct.target.perpendicular-from-point': 'Perpendicular from point',
  'construct.target.inscribed-triangle': 'Inscribed triangle',
  'construct.target.inscribed-square': 'Inscribed square',
  'construct.target.inscribed-hexagon': 'Inscribed hexagon',
  'construct.target.tangent-from-point': 'Tangent from point',
  'construct.target.incircle': 'Incircle',
  'construct.target.circumcircle': 'Circumcircle',
  'construct.target.angle-60': '60° angle',
  'construct.target.angle-30': '30° angle',
  'construct.target.angle-45': '45° angle',
  // Constructions — panel keys
  'construct.panel.target': 'Construction',
  'construct.panel.show-ghost': 'Show goal',
  // Constructions — readout keys
  'construct.readout.goal-met': 'Construction complete',
  'construct.readout.working': 'Working…',
  // Constructions — scripted step captions
  'perpBisector.arc1': 'Arc from A',
  'perpBisector.arc2': 'Arc from B',
  'perpBisector.line': 'Join intersections',
  'angleBisector.arcFromVertex': 'Arc from vertex',
  'angleBisector.arc2': 'Arc from arm A',
  'angleBisector.arc3': 'Arc from arm B',
  'angleBisector.line': 'Draw bisector',
  'copySegment.arc': 'Compass with segment length',
  'copyAngle.arc1': 'Arc at new vertex',
  'copyAngle.arc2': 'Copy chord — near side',
  'copyAngle.arc3': 'Copy chord — far side',
  'copyAngle.line': 'Draw copied angle ray',
  'perpAtPoint.arc1': 'Arc centred at P',
  'perpAtPoint.arc2': 'Large arc from left foot',
  'perpAtPoint.arc3': 'Large arc from right foot',
  'perpAtPoint.line': 'Join intersections',
  'perpFromPoint.arc1': 'Arc from external point',
  'perpFromPoint.arc2': 'Large arc from left foot',
  'perpFromPoint.arc3': 'Large arc from right foot',
  'perpFromPoint.line': 'Join intersections',
  'inscribedHex.s1': 'Step 1 — next vertex',
  'inscribedHex.s2': 'Step 2 — next vertex',
  'inscribedHex.s3': 'Step 3 — next vertex',
  'inscribedHex.s4': 'Step 4 — next vertex',
  'inscribedHex.s5': 'Step 5 — next vertex',
  'inscribedHex.side1': 'Draw side',
  'inscribedHex.side2': 'Draw side',
  'inscribedTri.s1': 'Arc — first vertex',
  'inscribedTri.s2': 'Arc — second vertex',
  'inscribedTri.s3': 'Arc — third vertex',
  'inscribedTri.side1': 'Side 1',
  'inscribedTri.side2': 'Side 2',
  'inscribedTri.side3': 'Side 3',
  'inscribedSquare.s1': 'Arc from P₀',
  'inscribedSquare.s2': 'Arc from antipode',
  'inscribedSquare.diam': 'Perpendicular diameter',
  'inscribedSquare.side1': 'Side 1',
  'inscribedSquare.side2': 'Side 2',
  'tangent.thalesCircle': 'Thales circle',
  'tangent.line1': 'First tangent',
  'tangent.line2': 'Second tangent',
  'incircle.arcA': 'Arc at vertex A',
  'incircle.arc2': 'Bisector arc 2',
  'incircle.arc3': 'Bisector arc 3',
  'incircle.bisA': 'Bisector at A',
  'incircle.arcB': 'Arc at vertex B',
  'incircle.arc6': 'Bisector arc 6',
  'incircle.arc7': 'Bisector arc 7',
  'incircle.bisB': 'Bisector at B',
  'incircle.footArc': 'Arc to find foot',
  'incircle.perpArc1': 'Perpendicular arc 1',
  'incircle.perpArc2': 'Perpendicular arc 2',
  'incircle.perpLine': 'Perpendicular line',
  'incircle.draw': 'Draw incircle',
  'circumcircle.arcA1': 'Arc A (1)',
  'circumcircle.arcA2': 'Arc A (2)',
  'circumcircle.bisAB': 'Bisect AB',
  'circumcircle.arcC1': 'Arc C (1)',
  'circumcircle.arcC2': 'Arc C (2)',
  'circumcircle.bisAC': 'Bisect AC',
  'circumcircle.draw': 'Draw circumcircle',
  'angle60.arc1': 'Arc at origin',
  'angle60.arc2': 'Arc at Q',
  'angle60.line': 'Draw 60° ray',
  'angle30.arc1': 'Arc at origin',
  'angle30.arc2': 'Arc at Q',
  'angle30.line60': 'Draw 60° line',
  'angle30.bisArc1': 'Bisector arc at origin',
  'angle30.bisArc2': 'Bisector arc 2',
  'angle30.bisArc3': 'Bisector arc 3',
  'angle30.line': 'Draw 30° ray',
  'angle45.perpArc1': 'Arc at origin',
  'angle45.perpArc2': 'Arc — near foot',
  'angle45.perpArc3': 'Arc — far foot',
  'angle45.line90': 'Draw 90° line',
  'angle45.bisArc1': 'Bisector arc at origin',
  'angle45.bisArc2': 'Bisector arc 2',
  'angle45.bisArc3': 'Bisector arc 3',
  'angle45.line': 'Draw 45° ray',
  // Per-experiment help legend lines
  'help.angle-chase.1': 'Drag the accent dots to open or close the angles.',
  'help.angle-chase.2': 'Each arc shows the angle at that vertex; equal ticks mark equal angles.',
  'help.angle-chase.3': 'Parallel arrows mark parallel rays; the readout proves the angle sum.',
  'help.triangle-lab.1': 'Drag the three accent vertices to reshape the triangle.',
  'help.triangle-lab.2': 'Side ticks mark equal sides; each arc marks an interior angle.',
  'help.triangle-lab.3':
    'The coloured dot is the chosen centre; the readout lists the measurements.',
  'help.pythagoras.1': 'Drag the accent vertices to change the legs a and b.',
  'help.pythagoras.2': 'A square or shape sits on each side, with its area written inside.',
  'help.pythagoras.3':
    'The readout shows a² + b² = c²: the two smaller areas add up to the largest.',
  'help.similarity.1': 'Drag the accent dots; the centre of dilation is the accent point.',
  'help.similarity.2': 'Rays from the centre map the dashed pre-image to the solid image.',
  'help.similarity.3': 'Scale factor k sets how much bigger the image is; area grows by k².',
  'help.circle-theorems.1': 'Drag the accent points around the circle.',
  'help.circle-theorems.2':
    'The arc marks the inscribed angle; the centre dot marks the central angle.',
  'help.circle-theorems.3': 'The readout proves the circle theorem for the current mode.',
  'help.area-lab.1': 'Pick a shape, then drag the sliders to change its size or shear.',
  'help.area-lab.2': 'The area is written on the shape; shearing keeps the area the same.',
  'help.area-lab.3': 'The derive slider rearranges the shape into a rectangle of equal area.',
  'help.transformations.1': 'The dashed shape is the pre-image; the accent shape is the image.',
  'help.transformations.2': 'In Symmetry, drag the mirror line — it glows on a true symmetry axis.',
  'help.transformations.3': 'Spin reveals rotational symmetry; the readout lists axes and order.',
  'help.coordinate-geometry.1': 'Drag the accent points A and B on the lattice.',
  'help.coordinate-geometry.2': 'The dashed triangle shows rise and run; the arc shows the angle.',
  'help.coordinate-geometry.3': 'The readout gives exact distances, slopes and areas.',
  'help.constructions.1': 'Pick a Tool (compass or straightedge) and a target construction.',
  'help.constructions.2': 'The faint dashed shape is the goal; snap dots show where you can click.',
  'help.constructions.3': 'When your drawing matches the goal it glows and the badge confirms it.',
};
